import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SocketProvider } from "./context/SocketContext";
import { Toaster } from "./components/ui/sonner";

// Public Pages (No Auth Required)
import LandingPage from "./pages/LandingPage";
import PublicMapPage from "./pages/PublicMapPage";
import PublicAlertsPage from "./pages/PublicAlertsPage";
import MapPrintView from "./pages/MapPrintView";

// Auth Pages
import LoginPage from "./pages/LoginPage";

// Personnel Pages (Auth Required)
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";
import ChatPage from "./pages/ChatPage";
import NotificationsPage from "./pages/NotificationsPage";

// Layout
import DashboardLayout from "./components/layout/DashboardLayout";

import "./App.css";

// Protected Route Component for Personnel
const ProtectedRoute = ({ children, allowedRoles, allowDispatch }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role OR is Dispatch personnel (for admin route)
  const isDispatch = user.role === 'personnel' && user.department === 'Dispatch';
  const hasAccess = !allowedRoles || allowedRoles.includes(user.role) || (allowDispatch && isDispatch);

  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      {/* ===================== */}
      {/* PUBLIC SIDE - No Auth */}
      {/* ===================== */}
      
      {/* Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Public Map - No login required */}
      <Route path="/map" element={<PublicMapPage />} />
      
      {/* Public Alerts - No login required */}
      <Route path="/alerts" element={<PublicAlertsPage />} />

      {/* Printable Map Report - No login required */}
      <Route path="/print-map" element={<MapPrintView />} />

      {/* ===================== */}
      {/* AUTH PAGES */}
      {/* ===================== */}
      
      {/* Login - Redirect to dashboard if already logged in */}
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage />} />

      {/* ========================= */}
      {/* PERSONNEL SIDE - Auth Required */}
      {/* ========================= */}
      
      {/* Dashboard - All authenticated users */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Admin Panel - Admin and Dispatch personnel */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["admin"]} allowDispatch={true}>
            <DashboardLayout>
              <AdminPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Chat - Personnel and Admin only */}
      <Route
        path="/chat"
        element={
          <ProtectedRoute allowedRoles={["admin", "personnel"]}>
            <DashboardLayout>
              <ChatPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Notifications - All authenticated users */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <NotificationsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Fallback - Redirect unknown routes to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SocketProvider>
          <div className="min-h-screen bg-background">
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </div>
        </SocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
